import {mapRecordToItems} from '@/helpers/mapRecordToItems.helper';

export enum CustomerMailTemplateTypes {
    ABSENCE_EARLY_WARNING = 'absenceEarlyWarning',
    /**
     * Single mail of an external sales partner to ONE school contact or the school address (Schul-Cockpit, sales-partner).
     * Placeholders: AcquisitionMailPlaceholders.
     */
    ACQUISITION_MAIL = 'acquisitionMail',
    COMMON_CUSTOMER_MAIL= 'commonCustomerMail',
    COURSE_CANCELLED_INSTITUTION = 'courseCancelledInstitution',
    COURSE_CANCELLED_STUDENT = 'courseCancelledStudent',
    COURSE_NOTIFICATION_INSTITUTION = 'courseNotificationInstitution',
    COURSE_NOTIFICATION_STUDENT = 'courseNotificationStudent',
    COURSE_NOTIFICATION_TEACHER = 'courseNotificationTeacher',
    /** Course pause (Elterninfo) */
    COURSE_PAUSED = 'coursePaused',
    /** Course restart after a pause (Elterninfo) */
    COURSE_RESUMED = 'courseResumed',
    FORM_SUBMISSION_CONFIRMATION = 'formSubmissionConfirmation',
    HANDOUT_EVENT_CONFIRMATION = 'handoutEventConfirmation',
    /** Terminwahl der Schule (Austeilrunde): link to the page where the school picks a day and checks its times */
    HANDOUT_EVENT_DATE_REQUEST = 'handoutEventDateRequest',
    HANDOUT_EVENT_FEEDBACK_REQUEST_ADVERTISER = 'handoutEventFeedbackRequestAdvertiser',
    HANDOUT_EVENT_FEEDBACK_REQUEST_INSTITUTION = 'handoutEventFeedbackRequestInstitution',
    HANDOUT_EVENT_REMINDER = 'handoutEventReminder',
    MAKEUP_PRESENCE_DATE = 'makeupPresenceDate',
    MAKEUP_ROUND_CONFIRMATION = 'makeupRoundConfirmation',
    MAKEUP_ROUND_INVITATION = 'makeupRoundInvitation',
    MISSING_DOCUMENTS_TEACHER = 'missingDocumentsTeacher',
    /** QM-Cockpit feedback request to the family of a new child (link to the answer page, unsubscribe link) */
    QM_FEEDBACK_CHILD = 'qmFeedbackChild',
    /** QM-Cockpit feedback request to a (new) teacher */
    QM_FEEDBACK_TEACHER = 'qmFeedbackTeacher',
    /** QM-Cockpit feedback request to a (new) Standort: Sekretariat / Ansprechpartner */
    QM_FEEDBACK_INSTITUTION = 'qmFeedbackInstitution',
    RETOURE_MAIL = 'retoureMail',
    SUBSTITUTION_REQUEST = 'substitutionRequest',
    /** Waiting list asked again after the office revoked the confirmation (same placeholders as SUBSTITUTION_REQUEST) */
    SUBSTITUTION_REQUEST_REOPENED = 'substitutionRequestReopened',
    /** "Vertretung bestätigt": thank-you to the substitute (date, time, Standort, personal link) */
    SUBSTITUTION_CONFIRMATION = 'substitutionConfirmation',
    TAKEOVER_ACCEPTANCE_REJECTED = 'takeoverAcceptanceRejected',
    TAKEOVER_OFFER = 'takeoverOffer',
    TAKEOVER_TEACHER_REPLACED = 'takeoverTeacherReplaced',
    TERMINATION= 'termination',
    TIMETABLE_INSTITUTION = 'timetableInstitution',
    TIMETABLE_STUDENT = 'timetableStudent',
    TIMETABLE_TEACHER = 'timetableTeacher',
    TRAINING_INVITATION = 'trainingInvitation',
}

export namespace CustomerMailTemplateTypes {
    type Data = {
        text: string;
    }

    export const Record: Record<CustomerMailTemplateTypes, Data> = {
        [CustomerMailTemplateTypes.ACQUISITION_MAIL]: {
            text: 'Akquise-Mail (Salespartner)',
        },
        [CustomerMailTemplateTypes.ABSENCE_EARLY_WARNING]: {
            text: 'Frühwarnung bei Abwesenheiten (an Familien)',
        },
        [CustomerMailTemplateTypes.COMMON_CUSTOMER_MAIL]: {
            text: 'Allgemein (für Eltern)',
        },
        [CustomerMailTemplateTypes.COURSE_CANCELLED_INSTITUTION]: {
            text: 'Kursabsage für Standorte',
        },
        [CustomerMailTemplateTypes.COURSE_CANCELLED_STUDENT]: {
            text: 'Kursabsage für Schüler:innen',
        },
        [CustomerMailTemplateTypes.COURSE_NOTIFICATION_INSTITUTION]: {
            text: 'Kursbenachrichtigung für Standorte',
        },
        [CustomerMailTemplateTypes.COURSE_NOTIFICATION_STUDENT]: {
            text: 'Kursbenachrichtigung für Schüler:innen',
        },
        [CustomerMailTemplateTypes.COURSE_NOTIFICATION_TEACHER]: {
            text: 'Kursbenachrichtigung für Lehrkräfte',
        },
        [CustomerMailTemplateTypes.FORM_SUBMISSION_CONFIRMATION]: {
            text: 'Bestätigung einer Formularübermittlung',
        },
        [CustomerMailTemplateTypes.HANDOUT_EVENT_CONFIRMATION]: {
            text: 'Bestätigung des Termins einer Austeilaktion',
        },
        [CustomerMailTemplateTypes.HANDOUT_EVENT_DATE_REQUEST]: {
            text: 'Terminwahl einer Austeilaktion (an Schulen)',
        },
        [CustomerMailTemplateTypes.HANDOUT_EVENT_FEEDBACK_REQUEST_ADVERTISER]: {
            text: 'Feedback-Anfrage für Werbende',
        },
        [CustomerMailTemplateTypes.HANDOUT_EVENT_FEEDBACK_REQUEST_INSTITUTION]: {
            text: 'Feedback-Anfrage für Standorte',
        },
        [CustomerMailTemplateTypes.HANDOUT_EVENT_REMINDER]: {
            text: 'Erinnerung an eine Austeilaktion',
        },
        [CustomerMailTemplateTypes.MAKEUP_PRESENCE_DATE]: {
            text: 'Nachholtermin vor Ort (an Familien)',
        },
        [CustomerMailTemplateTypes.MAKEUP_ROUND_CONFIRMATION]: {
            text: 'Online-Nachholtermin bestätigt (Uhrzeit, Zoom, an Eltern)',
        },
        [CustomerMailTemplateTypes.MAKEUP_ROUND_INVITATION]: {
            text: 'Angebot Online-Nachholtermin (Ja-Link, an Eltern)',
        },
        [CustomerMailTemplateTypes.MISSING_DOCUMENTS_TEACHER]: {
            text: 'Fehlende Dokumente - Lehrer',
        },
        [CustomerMailTemplateTypes.QM_FEEDBACK_CHILD]: {
            text: 'QM-Feedbackanfrage (an Familien)',
        },
        [CustomerMailTemplateTypes.QM_FEEDBACK_TEACHER]: {
            text: 'QM-Feedbackanfrage (Lehrkraft)',
        },
        [CustomerMailTemplateTypes.QM_FEEDBACK_INSTITUTION]: {
            text: 'QM-Feedbackanfrage (Standort)',
        },
        [CustomerMailTemplateTypes.RETOURE_MAIL]: {
            text: 'Retoure E-Mail',
        },
        [CustomerMailTemplateTypes.SUBSTITUTION_REQUEST]: {
            text: 'Vertretungsanfrage',
        },
        [CustomerMailTemplateTypes.SUBSTITUTION_REQUEST_REOPENED]: {
            text: 'Vertretungsanfrage wieder frei (Warteliste)',
        },
        [CustomerMailTemplateTypes.SUBSTITUTION_CONFIRMATION]: {
            text: 'Vertretung bestätigt (Lehrkraft)',
        },
        [CustomerMailTemplateTypes.TAKEOVER_ACCEPTANCE_REJECTED]: {
            text: 'Übernahmeangebot: Zusage abgelehnt (Lehrkraft)',
        },
        [CustomerMailTemplateTypes.TAKEOVER_OFFER]: {
            text: 'Übernahmeangebot (Lehrkraft)',
        },
        [CustomerMailTemplateTypes.COURSE_PAUSED]: {
            text: 'Kurs pausiert (an Familien)',
        },
        [CustomerMailTemplateTypes.COURSE_RESUMED]: {
            text: 'Unterricht wieder aufgenommen (an Familien)',
        },
        [CustomerMailTemplateTypes.TAKEOVER_TEACHER_REPLACED]: {
            text: 'Übernahmeangebot: bisherige Lehrkraft ersetzt',
        },
        [CustomerMailTemplateTypes.TERMINATION]: {
            text: 'Kündigung',
        },
        [CustomerMailTemplateTypes.TIMETABLE_INSTITUTION]: {
            text: 'Stundenplan für Standorte',
        },
        [CustomerMailTemplateTypes.TIMETABLE_STUDENT]: {
            text: 'Stundenplan für Schüler:innen',
        },
        [CustomerMailTemplateTypes.TIMETABLE_TEACHER]: {
            text: 'Stundenplan für Lehrkräfte',
        },
        [CustomerMailTemplateTypes.TRAINING_INVITATION]: {
            text: 'Einladung zu einer Schulung',
        },
    }

    export const Items = mapRecordToItems(Record);
    Items.sort((a, b) => a.text.localeCompare(b.text));
}

/** Mail template type of a QM feedback request per target group (QualityOnboardingTarget value) */
export const QM_FEEDBACK_MAIL_TEMPLATE_TYPES: Record<'child' | 'teacher' | 'institution', CustomerMailTemplateTypes> = {
    child: CustomerMailTemplateTypes.QM_FEEDBACK_CHILD,
    teacher: CustomerMailTemplateTypes.QM_FEEDBACK_TEACHER,
    institution: CustomerMailTemplateTypes.QM_FEEDBACK_INSTITUTION,
};
